import {plural, t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {TextArea} from "@khanacademy/wonder-blocks-form";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {RequestInfo} from "@khan/shared-core";

// sync-start:max-bio-length 1404380509 https://github.com/Khan/webapp/blob/master/services/users/mutations/set_settings.go
const MAX_BIO_LENGTH = 160;
// sync-end:max-bio-length

const strings = {
    // For displaying and styling the count of characters remaining
    charsLeftText: (charsLeft: number) =>
        // TODO(davemcalpine): remove this check once translations are complete
        RequestInfo.kaLocale === "en"
            ? plural(charsLeft, {
                  one: "# character left",
                  other: "# characters left",
              })
            : plural(charsLeft, {
                  one: "# char left",
                  other: "# chars left",
              }),
    placeholder: (maxChars: number) =>
        t`Tell the Khan Academy community about yourself in ${maxChars} characters or less.  Who are you? How are you using Khan Academy?`,
} as const;

type Props = {
    // Maximum # of characters allowed in the textarea
    MAX_CHARS: number;
    // An ID to use for the input control
    id: string;
    value: string;
    onBlur: () => unknown;
    onChange: (value: string) => unknown;
};

/**
 * Maximum-character textarea for the user's public "bio" string
 */
class BioPicker extends React.Component<Props> {
    static defaultProps: {
        MAX_CHARS: number;
        onBlur: () => void;
    } = {
        onBlur: function () {},
        MAX_CHARS: MAX_BIO_LENGTH,
    };

    handleKeyDown: (e: React.KeyboardEvent) => undefined | boolean = (
        e: React.KeyboardEvent,
    ) => {
        // The user tries to type a newline character. We silently prohibit
        // it.
        if (e.keyCode === 13) {
            return false; // Don't let the "change" event fire
        }
    };

    handleChange = (value: string) => {
        let text = value;

        // In pasted text, silently replace each newline character with a
        // space. (Users are prohibited from typing newline characters
        // directly in handleKeyDown.)
        text = text.replace(/\n/g, " ");

        if (this.props.onChange) {
            this.props.onChange(text);
        }
    };

    render(): React.ReactNode {
        const MAX_CHARS = this.props.MAX_CHARS;
        const {value, onBlur} = this.props;

        const charsLeft = MAX_CHARS - value.length;
        const charsLeftClass = charsLeft === 0 ? "error" : "neutral";

        return (
            <div className="user-bio-edit-view">
                <TextArea
                    maxLength={MAX_CHARS}
                    onKeyDown={this.handleKeyDown}
                    onChange={this.handleChange}
                    onBlur={onBlur}
                    placeholder={strings.placeholder(MAX_CHARS)}
                    value={value}
                    id={this.props.id}
                    style={styles.textArea}
                    resizeType="none"
                    rows={3}
                />
                <span className={"help-block " + charsLeftClass}>
                    <span className="left chars-left">
                        {strings.charsLeftText(charsLeft)}
                    </span>
                </span>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    textArea: {
        marginBottom: spacing.xxxSmall_4,
    },
});

export default BioPicker;
